import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  Users,
  Building2,
  Clock,
  Calendar,
  CreditCard,
  UserPlus,
  GraduationCap,
  MessageSquare,
  Megaphone,
  Settings,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../hooks/use-auth";

const navigation = [
  {
    name: "dashboard",
    href: "/dashboard",
    employeeHref: "/employee-dashboard", // Special href for employees
    icon: Home,
    roles: ["admin", "rh", "manager", "employe", "stagiaire"],
  },
  {
    name: "employees",
    href: "/dashboard/employees",
    icon: Users,
    roles: ["admin", "rh", "manager"],
  },
  {
    name: "departments",
    href: "/dashboard/departments",
    icon: Building2,
    roles: ["admin", "rh"],
  },
  {
    name: "attendance",
    href: "/dashboard/attendance",
    icon: Clock,
    roles: ["admin", "rh", "manager", "employe", "stagiaire"],
  },
  {
    name: "leave",
    href: "/dashboard/leave-management",
    icon: Calendar,
    roles: ["admin", "rh", "manager", "employe"],
  },
  {
    name: "payroll",
    href: "/dashboard/payroll",
    icon: CreditCard,
    roles: ["admin", "rh", "employe"],
  },
  {
    name: "recruitment",
    href: "/dashboard/recruitment",
    icon: UserPlus,
    roles: ["admin", "rh"],
  },
  {
    name: "training",
    href: "/dashboard/training",
    icon: GraduationCap,
    roles: ["admin", "rh", "manager", "employe"],
  },
  {
    name: "messages",
    href: "/dashboard/messages",
    icon: MessageSquare,
    roles: ["admin", "rh", "manager", "employe", "stagiaire"],
  },
  {
    name: "announcements",
    href: "/dashboard/announcements",
    icon: Megaphone,
    roles: ["admin", "rh", "manager", "employe", "stagiaire"],
  },
  {
    name: "settings",
    href: "/dashboard/settings",
    icon: Settings,
    roles: ["admin", "rh", "manager", "employe", "stagiaire"],
  },
];

export default function Sidebar({
  open,
  setOpen,
  collapsed,
  setCollapsed,
  user,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter((item) =>
    item.roles.includes(user?.role)
  );

  // Get the correct href based on user role
  const getHrefForUser = (item) => {
    if (item.employeeHref && user?.role?.toLowerCase() === "employe") {
      return item.employeeHref;
    }
    return item.href;
  };

  const isCurrentPath = (href) => {
    if (href === "/dashboard") {
      return location.pathname === href;
    }
    if (href === "/employee-dashboard") {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={cn(
          "hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 transition-all duration-300 z-30",
          collapsed ? "lg:w-16" : "lg:w-64"
        )}
      >
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-sm">
          {/* Logo Section */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
            {!collapsed && (
              <div className="flex items-center">
                <img
                  className="h-12 w-auto"
                  src="/omnidoc-logo.png"
                  alt="Logo"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                {/* <span className="ml-3 text-lg font-bold text-gray-900 dark:text-white">
                  HR System
                </span> */}
              </div>
            )}

            {/* Collapse Toggle */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const itemHref = getHrefForUser(item);
              const isActive = isCurrentPath(itemHref);

              return (
                <button
                  key={item.name}
                  onClick={() => navigate(itemHref)}
                  className={cn(
                    "w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200",
                    isActive
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-r-2 border-blue-500"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                    collapsed && "justify-center"
                  )}
                  title={collapsed ? t(`navigation.${item.name}`) : undefined}
                >
                  <item.icon
                    className={cn(
                      "h-5 w-5",
                      isActive
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-gray-500 dark:text-gray-400",
                      !collapsed && "mr-3"
                    )}
                  />
                  {!collapsed && <span>{t(`navigation.${item.name}`)}</span>}
                </button>
              );
            })}
          </nav>

          {/* User Info (when not collapsed) */}
          {!collapsed && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                    {user?.prenom?.charAt(0)}
                    {user?.nom?.charAt(0)}
                  </div>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.prenom} {user?.nom}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.role}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div
        className={cn(
          "lg:hidden fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ease-in-out",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-lg">
          {/* Logo Section */}
          <div className="flex items-center h-16 px-4 border-b border-gray-200 dark:border-gray-700">
            <img
              className="h-8 w-auto"
              src="/omnidoc-logo.png"
              alt="Logo"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <span className="ml-3 text-lg font-bold text-gray-900 dark:text-white">
              HR System
            </span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const itemHref = getHrefForUser(item);
              const isActive = isCurrentPath(itemHref);

              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(itemHref);
                    setOpen(false); // Close mobile menu
                  }}
                  className={cn(
                    "w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200",
                    isActive
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-r-2 border-blue-500"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  )}
                >
                  <item.icon
                    className={cn(
                      "h-5 w-5 mr-3",
                      isActive
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-gray-500 dark:text-gray-400"
                    )}
                  />
                  <span>{t(`navigation.${item.name}`)}</span>
                </button>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                  {user?.prenom?.charAt(0)}
                  {user?.nom?.charAt(0)}
                </div>
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.prenom} {user?.nom}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}



























